/**
 * The live-draft strip — turn `10a`/`10b`.
 *
 * While the draft is running, a manager who has left the board to look up a rule needs three
 * things without going back: who is picking, how far away his own pick is, and a way in. The
 * design's answer is a strip that follows him through the whole app, escalating in three steps —
 * a quiet dark bar, then his position in the queue, then red with a single button.
 *
 * This module is only the decision: WHICH state, and the numbers each state states. Rendering it
 * is `src/ui/DraftStrip.tsx`. Keeping the two apart is what lets the escalation be tested — the
 * states are three lines apart in the code and a long way apart on screen, and getting "yours"
 * wrong by one pick on draft night is the kind of bug nobody forgives.
 *
 * THE CLOCK IS ELAPSED, NOT REMAINING. The drawing shows `0:54` and calls it a clock; a countdown
 * would need a per-pick allowance this league has never had — they draft in a room, and nobody
 * has ever been timed out. What IS knowable is how long the current pick has been sitting, from
 * the previous pick's own timestamp, and that answers the question the number is there for:
 * is it about to be my turn or has Ryan gone to get a beer. Where there is no previous pick to
 * measure from, there is no clock — blank beats partial.
 */

import { pickCode, whoIsUp, type BoardPick } from './board.js';

export type StripState = 'off' | 'away' | 'ondeck' | 'yours';

export interface StripInfo {
  state: StripState;
  /** The pick being made right now. */
  onClock: { code: string; round: number; owner: string } | null;
  /** Your next pick, and how many picks until it. Zero away means it IS yours. */
  mine: { code: string; away: number } | null;
  /** When the previous pick was made, for the elapsed clock. Null when unmeasurable. */
  since: string | null;
}

const OFF: StripInfo = { state: 'off', onClock: null, mine: null, since: null };

export interface StripInput {
  status: 'setup' | 'live' | 'complete' | null | undefined;
  board: readonly BoardPick[];
  teams: number;
  /** The manager whose screen this is. */
  me: string;
  /** GM → the manager covering for him tonight, if any. A cover gets the red bar too. */
  designates?: Readonly<Record<string, string>>;
  /**
   * WHAT THE STRIP NEEDS TO ANSWER "WHO IS UP" THE WAY THE BOARD DOES (Tim, 8 September:
   * "impersonating matt while Ryan is on the clock but his home screen says John").
   *
   * Without a clock the strip has no way to know a pick has lapsed, so it named the first unmade
   * cell — and on a board with two lapses behind the frontier that is a man the draft went past
   * minutes ago. The shell strip and Home's draft card both read this, so both were wrong in the
   * same way while the board beside them was right.
   *
   * All optional: a league with no pick bands has no lapses to reason about, and the first
   * unmade pick is then the correct answer as it always was.
   */
  secondsFor?: (round: number) => number | null;
  restartedAt?: string | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  pauses?: readonly { at: string; until?: string | null }[];
  now?: number;
}

export function draftStrip(
  { status, board, teams, me, designates = {},
    secondsFor, restartedAt = null, startedAt = null, updatedAt = null,
    pauses = [], now = Date.now() }: StripInput,
): StripInfo {
  if (status !== 'live' || board.length === 0 || !me) return OFF;

  const room = secondsFor
    ? whoIsUp({ board, status, secondsFor, restartedAt, startedAt, updatedAt, pauses, now })
    : null;
  /*
   * In a jam every open pick has run out and there is nobody up at all. The strip's job is to
   * tell a manager where the room is and whether it is on him, and the honest answer then is the
   * earliest cell still outstanding — which is what `whoIsUp` falls back to.
   */
  const onClock = room?.clock ?? board.find((p) => !p.player);
  if (!onClock) return OFF;

  const actor = me.toUpperCase();
  /*
   * "Yours" is about OWNERSHIP, not permission. A commissioner can type into any cell, but a red
   * bar on every pick in the draft would make the loudest state in the app the normal one — so
   * the strip escalates for the man whose pick it is and for whoever is covering for him.
   */
  const covering = new Set(
    Object.entries(designates)
      .filter(([, to]) => (to ?? '').toUpperCase() === actor)
      .map(([gm]) => gm.toUpperCase()),
  );
  const isMine = (p: BoardPick) => {
    const owner = p.owner.toUpperCase();
    return owner === actor || covering.has(owner);
  };

  const next = board.find((p) => !p.player && isMine(p)) ?? null;
  const away = next ? next.overall - onClock.overall : null;
  /* A lapsed pick of his own is not "-2 away": it is open, and he can type into it right now. */

  /*
   * The elapsed figure counts from when THIS pick went on the clock, not from the newest stamp
   * on the board — the same frontier reading the board uses, for the same reason.
   */
  const made = board.filter((p) => p.player && p.madeAt);
  const opened = room?.open.find((o) => o.pick.overall === onClock.overall);
  const since = opened ? new Date(opened.startedAt).toISOString()
    : made.length
      ? made.reduce((latest, p) => (p.madeAt! > latest ? p.madeAt! : latest), made[0]!.madeAt!)
      : null;

  const info = {
    onClock: {
      code: pickCode(onClock, teams),
      round: onClock.round,
      owner: onClock.owner,
    },
    mine: next && away !== null ? { code: pickCode(next, teams), away } : null,
    since,
  };

  if (away === 0) return { ...info, state: 'yours' };
  // "On deck" is the drawing's own window: it labels the two-picks-out case, and one pick out is
  // more urgent still, not less.
  if (away !== null && away <= 2) return { ...info, state: 'ondeck' };
  return { ...info, state: 'away' };
}

/**
 * `M:SS` since a timestamp. Caps at 59:59 rather than rolling over into hours — past an hour on
 * one pick the number has stopped being a clock and become a complaint, and the drawing has room
 * for four characters.
 */
export function elapsed(since: string | null | undefined, now = Date.now()): string | null {
  if (!since) return null;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return null;
  const secs = Math.max(0, Math.floor((now - t) / 1000));
  if (secs >= 3600) return '59:59';
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}
